import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { ApiError } from '../lib/query.js';

interface ThreadMessage {
  id: string;
  from: 'client' | 'pro';
  text: string;
  ts: string;
}

interface ThreadResponse {
  messages: ThreadMessage[];
}

interface SendResponse {
  ok: true;
  inbound: ThreadMessage;
  outbound: ThreadMessage;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DemoClientRoute() {
  const [params] = useSearchParams();
  const clientPhone = params.get('phone') ?? '+33612345678';
  const proName = params.get('pro') ?? 'Karim — Coiffure';

  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const out = await api<ThreadResponse>(
          `/demo/thread?clientPhone=${encodeURIComponent(clientPhone)}`,
        );
        if (!cancelled) setMessages(out.messages);
      } catch {
        // silent — keep last state, retry next tick
      }
    }
    void poll();
    const id = setInterval(() => void poll(), 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [clientPhone]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  async function handleSend() {
    if (!text.trim() || sending) return;
    setSending(true);
    setPipelineError(null);
    const draft = text.trim();
    setText('');
    try {
      const res = await api<SendResponse>('/demo/send', {
        method: 'POST',
        json: { clientPhone, text: draft },
      });
      setMessages((prev) => {
        const next = [...prev];
        if (!next.some((m) => m.id === res.inbound.id)) next.push(res.inbound);
        if (!next.some((m) => m.id === res.outbound.id)) next.push(res.outbound);
        return next;
      });
    } catch (err) {
      const apiErr = err as ApiError;
      setPipelineError(apiErr.message || 'Erreur pipeline');
    } finally {
      setSending(false);
    }
  }

  async function handleReset() {
    await api('/demo/reset', { method: 'POST' });
    setMessages([]);
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#000]">
      {/* iOS-Messages-like header (green for SMS, blue for iMessage; we use green for RCS) */}
      <header className="bg-[#1c1c1e] text-white pt-2 pb-3 px-4 flex items-center justify-between border-b border-[#38383a]">
        <button
          type="button"
          onClick={handleReset}
          className="text-[#0a84ff] font-sans text-sm"
          aria-label="Réinitialiser le fil"
        >
          ←
        </button>
        <div className="flex flex-col items-center">
          <div className="w-9 h-9 rounded-full bg-[#34c759] grid place-items-center text-[#0b1f12] font-mono text-sm font-semibold">
            K
          </div>
          <span className="text-[11px] mt-1 font-sans tracking-tight text-white">{proName}</span>
          <span className="text-[9px] text-[#8e8e93] font-mono">RCS · {clientPhone}</span>
        </div>
        <button
          type="button"
          className="text-[#0a84ff] font-sans text-sm opacity-50"
          disabled
          aria-label="Infos contact"
        >
          ⓘ
        </button>
      </header>

      {/* Thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 bg-[#000] space-y-2">
        {messages.length === 0 && (
          <p className="text-center text-[#8e8e93] text-xs font-sans mt-12">
            Démarre la conversation. Tape un message comme si tu écrivais à ton coiffeur.
          </p>
        )}
        {messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const showTime = !prev || new Date(m.ts).getTime() - new Date(prev.ts).getTime() > 60_000;
          const isClient = m.from === 'client';
          return (
            <div key={m.id}>
              {showTime && (
                <div className="text-center text-[10px] text-[#8e8e93] font-mono py-2">
                  {formatTime(m.ts)}
                </div>
              )}
              <div className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[78%] px-3 py-2 rounded-2xl text-[15px] font-sans leading-snug ${
                    isClient
                      ? 'bg-[#0a84ff] text-white rounded-br-md'
                      : 'bg-[#262629] text-white rounded-bl-md'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#262629] text-[#8e8e93] px-3 py-2 rounded-2xl rounded-bl-md text-[13px] font-sans">
              …
            </div>
          </div>
        )}
        {pipelineError && (
          <div className="text-center text-[11px] text-[#ff453a] font-mono py-2">
            {pipelineError}
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="bg-[#1c1c1e] border-t border-[#38383a] px-3 py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] flex items-end gap-2">
        <div className="flex-1 bg-[#2c2c2e] rounded-2xl px-3 py-2 flex items-center">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder="Texte"
            className="flex-1 bg-transparent outline-none text-white text-[15px] font-sans placeholder:text-[#8e8e93]"
            inputMode="text"
            autoCorrect="on"
            autoCapitalize="sentences"
          />
        </div>
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!text.trim() || sending}
          className="w-8 h-8 rounded-full bg-[#0a84ff] text-white grid place-items-center disabled:opacity-40 transition-opacity"
          aria-label="Envoyer"
        >
          <span className="-mt-[2px] text-base">↑</span>
        </button>
      </div>
    </div>
  );
}
